// Panel primitives — the single home for the sidebar-panel look. Every panel
// row, label, divider, search box, badge and state message in every app's hover
// panel renders through these, so a tweak here lands everywhere at once and the
// panels can't drift apart. No business logic lives here.
//
// The object-driven engine composes these from JSON; the panels not yet
// migrated to the engine import the same components rather than keeping their
// own copies.

import { createContext, useContext, type ComponentType, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, Search, Star, X } from 'lucide-react';
import { Button, cn } from '@clarix/ui';
import { HighlightText } from '@clarix/ui-shared';
import type { BadgeKind, BadgeTone } from '@clarix/shared-types';
import { TONE_PILL } from './tones';
import { usePanelFavourites, type FavouriteCandidate } from './favouritesContext';
import { PANEL_ROW_CLASS } from './rowStyles';

interface IconProps {
  className?: string;
  strokeWidth?: number;
}

/* ── Nav row ── */

interface HoverNavRowProps {
  to: string;
  onNavigate: () => void;
  active?: boolean;
  /**
   * How the active row reads:
   *  - `fill` (default) — indigo background + indigo text (the project rows).
   *  - `text` — indigo text only, no background (the tab sub-rows).
   */
  activeVariant?: 'fill' | 'text';
  /** Extra side-effect to run on click, before navigating (e.g. expand a tree node). */
  onClick?: () => void;
  /**
   * Action rendered at the row's trailing edge, ALWAYS visible (not hover-only
   * — a delete affordance you can't see is a delete affordance you can't find).
   * Sits OUTSIDE the nav button — a button can't be nested inside a button, and
   * the click must not also navigate. Omit it and the row's markup is unchanged.
   */
  trailing?: ReactNode;
  /**
   * Makes the row starrable inside a panel that provides favourites. `menu` rows
   * keep a filled star while starred; rows in the Favourites section show it on hover only.
   */
  favourite?: { id: string; label: string; placement?: 'menu' | 'favourites' };
  /** Layout-only extras (e.g. the tree's child indent), merged last. */
  className?: string;
  children: ReactNode;
}

/**
 * Lets {@link PanelRowIcon} read its row's active state without every call site
 * threading the same expression through twice (once to the row, once to the
 * icon). Deliberately NOT exported — it is an implementation detail of the two
 * components below.
 */
const HoverNavRowActiveContext = createContext(false);

/**
 * The leading icon of a panel nav row. Takes the active indigo as its STROKE
 * colour and reads muted otherwise — the one place that look is defined, for
 * every panel in every app.
 *
 * Deliberately not filled: the active row already carries an indigo background
 * and indigo label, so filling the glyph too made it read as a third, heavier
 * emphasis on the same row — and it flattened outline icons (a folder, a
 * clipboard) into solid blocks that no longer matched their inactive shape.
 * Colour alone is the state; the silhouette stays constant.
 *
 * Only the row's LEADING icon should use this. Icons that carry their own
 * meaning (a project's custom colour, a pinned marker, a semantic status tint)
 * are not row chrome and must keep their own colour.
 */
export function PanelRowIcon({
  icon: Icon,
  active: activeProp,
  className,
}: {
  icon: ComponentType<IconProps>;
  /** Override for rows that own their button instead of using `HoverNavRow`. */
  active?: boolean;
  className?: string;
}) {
  const rowActive = useContext(HoverNavRowActiveContext);
  const active = activeProp ?? rowActive;
  return (
    <Icon
      className={cn(
        'h-4 w-4 flex-shrink-0',
        active ? 'text-indigo-700' : 'text-gray-400',
        className,
      )}
      strokeWidth={1.5}
    />
  );
}

export function HoverNavRow({
  to,
  onNavigate,
  active,
  activeVariant = 'fill',
  onClick,
  trailing,
  favourite,
  className,
  children,
}: HoverNavRowProps) {
  const navigate = useNavigate();
  const favourites = usePanelFavourites();
  // A row owns one trailing slot: an explicit action wins over the star.
  const fav = !trailing && favourite && favourites ? favourite : null;
  const starred = fav ? favourites!.isFavourite(fav.id) : false;
  const starPinned = starred && fav?.placement !== 'favourites';

  const row = (
    <button
      type="button"
      onClick={() => {
        onClick?.();
        navigate(to);
        onNavigate();
      }}
      className={cn(
        // `group` so a child can reveal itself on row hover (`group-hover:…`) —
        // used by the pay-period rows for their trailing chevron.
        'group',
        PANEL_ROW_CLASS,
        'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
        // The trailing action is absolutely positioned (so it never shrinks the
        // label); reserve its width so a long label truncates before it, not under it.
        (trailing || starPinned) && 'pr-8',
        fav && !starPinned && 'group-focus-within/row:pr-8 group-hover/row:pr-8',
        active &&
          (activeVariant === 'text'
            ? 'font-medium text-indigo-700'
            : 'bg-indigo-50 font-medium text-indigo-700'),
        className,
      )}
    >
      <HoverNavRowActiveContext.Provider value={!!active}>
        {children}
      </HoverNavRowActiveContext.Provider>
    </button>
  );

  if (!trailing && !fav) return row;

  return (
    <div className="group/row relative flex min-w-0 items-center">
      {row}
      <div className="absolute right-1.5 flex items-center">
        {trailing ??
          (fav && (
            <button
              type="button"
              aria-pressed={starred}
              aria-label={
                starred ? `Remove ${fav.label} from favourites` : `Add ${fav.label} to favourites`
              }
              onClick={() => favourites!.toggle({ id: fav.id, label: fav.label, to })}
              // Inside a draggable tree row; opts out of `.sidebar-grab`'s cursor.
              data-grab-exempt
              className={cn(
                'flex h-5 w-5 cursor-pointer items-center justify-center rounded transition-opacity',
                'focus-visible:ring-ring focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1',
                !starPinned && 'opacity-0 group-hover/row:opacity-100',
              )}
            >
              <Star
                className={cn(
                  'h-3.5 w-3.5',
                  starred ? 'fill-star text-star' : 'text-muted-foreground hover:text-star',
                )}
                strokeWidth={1.5}
              />
            </button>
          ))}
      </div>
    </div>
  );
}

/**
 * The panel's "Favourites" group. Resolves each starred id against the rows the
 * panel can show RIGHT NOW, so a favourite whose row lost its permission disappears.
 */
export function FavouritesSection({
  candidates,
  onNavigate,
  query = '',
}: {
  candidates: readonly FavouriteCandidate[];
  onNavigate: () => void;
  query?: string;
}) {
  const favourites = usePanelFavourites();
  if (!favourites || favourites.items.length === 0) return null;

  const live = new Map(candidates.map((c) => [c.id, c]));
  const q = query.trim().toLowerCase();
  const rows = favourites.items
    .map((f) => live.get(f.id))
    .filter((c): c is FavouriteCandidate => !!c && (!q || c.label.toLowerCase().includes(q)));
  if (rows.length === 0) return null;

  return (
    <div className="mt-4 first:mt-0">
      <PanelSectionHeader label="Favourites" />
      <div className="space-y-0.5 px-2">
        {rows.map((c) => (
          <HoverNavRow
            key={c.id}
            to={c.to}
            onNavigate={onNavigate}
            active={c.active}
            favourite={{ id: c.id, label: c.label, placement: 'favourites' }}
          >
            <HighlightText text={c.label} query={query} className="min-w-0 flex-1 truncate" />
          </HoverNavRow>
        ))}
      </div>
    </div>
  );
}

/* ── Search input ── */

export function SidebarSearchInput({
  value,
  onChange,
  placeholder = 'Search...',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="mb-1 px-2">
      <div className="relative">
        <Search
          className="text-muted-foreground absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2"
          strokeWidth={1.5}
        />
        <input
          type="text"
          placeholder={placeholder}
          aria-label={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-muted placeholder:text-muted-foreground h-8 w-full rounded-lg border border-transparent pl-8 pr-7 text-lg focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Section chrome ── */

export function SectionLabel({ children }: { children: ReactNode }) {
  return <span className="text-muted-foreground text-md font-medium">{children}</span>;
}

/**
 * A group's heading row. `px-4` puts the label on the same vertical as the row
 * icons (row container `px-2` + row `px-2`), the alignment the panel is built on.
 */
export function PanelSectionHeader({
  label,
  control,
  className,
}: {
  label?: ReactNode;
  control?: ReactNode;
  className?: string;
}) {
  if (!label && !control) return null;
  return (
    <div className={cn('mb-1 flex min-h-5 items-center justify-between gap-2 px-4', className)}>
      {label ? <SectionLabel>{label}</SectionLabel> : <span />}
      {control}
    </div>
  );
}

/* ── Toggle pill (the "My projects" scope switch) ── */

export function PanelToggle({
  label,
  ariaLabel,
  checked,
  onChange,
}: {
  label: string;
  ariaLabel?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <Button
      type="button"
      variant="secondary"
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel ?? label}
      onClick={() => onChange(!checked)}
      className={cn(
        'h-auto gap-1 rounded-sm px-1 py-1 [&_svg]:size-2.5',
        checked
          ? 'border-indigo-200 bg-indigo-50 hover:bg-indigo-100'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
      )}
    >
      <span
        className={cn(
          'flex h-3 w-3 flex-shrink-0 items-center justify-center rounded-[3px] border transition-colors',
          checked ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-gray-300 bg-white',
        )}
      >
        {checked && <Check strokeWidth={3} />}
      </span>
      <span
        className={cn(
          'whitespace-nowrap text-xs font-medium leading-none',
          checked ? 'text-indigo-600' : 'text-gray-500',
        )}
      >
        {label}
      </span>
    </Button>
  );
}

/* ── Loading / empty / error states ── */

export function PanelLoading() {
  return (
    <div className="flex justify-center py-3">
      <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
    </div>
  );
}

export function PanelEmpty({ children }: { children: ReactNode }) {
  return <div className="text-muted-foreground px-3 py-3 text-center text-sm">{children}</div>;
}

export function PanelError({ children }: { children: ReactNode }) {
  return <div className="px-3 py-3 text-center text-sm text-red-500">{children}</div>;
}

/* ── Badge ── */

export function PanelBadge({
  kind,
  value,
  tone = 'gray',
}: {
  kind: BadgeKind;
  value: number;
  tone?: BadgeTone;
}) {
  // A list's size is reference information, not a call to action — it reads as
  // a plain number so it can't compete with the row's label.
  if (kind === 'plain') {
    return <span className="text-muted-foreground ml-auto flex-shrink-0 text-xs">{value}</span>;
  }
  if (kind === 'traffic-light') {
    return (
      <span
        className={cn(
          'flex h-[18px] min-w-[18px] flex-shrink-0 items-center justify-center rounded-full px-1 text-xs font-semibold',
          TONE_PILL[tone],
        )}
      >
        {value}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'ml-auto flex h-5 min-w-[20px] flex-shrink-0 items-center justify-center rounded-full px-1.5 text-sm font-semibold leading-none',
        TONE_PILL[tone],
      )}
    >
      {value}
    </span>
  );
}

/** Trailing coloured text pill (project role badge, ARCHIVED, …). */
export function PanelPill({
  label,
  className,
  title,
}: {
  label: string;
  className: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        'text-xxs flex-shrink-0 whitespace-nowrap rounded px-1 py-px font-bold leading-none',
        className,
      )}
    >
      {label}
    </span>
  );
}
